<template>
  <VDialogV3
    id="contract-external-card"
    position="right"
    width="600px"
    height="100vh"
    :context-menu="ContractsExternalService.contextMenu"
    :entity="localContract"
    :tabs="tabs"
    v-model:active-tab="activeTab"
    :submit-disabled="submitDisabled"
    :submit-method="submit"
  >
    <FilesList
      v-if="localContract.isExisting && activeTab.id === 'files'"
      :entities="fileEntities"
    />

    <template v-else>
      <section>
        <VFormItem
          label="Номер"
          layout="vertical"
          with-asterisk
        >
          <VInput
            v-model="localContract.number"
            required
            :disabled="localContract.isExisting"
          />

          <span>от</span>

          <VDateInput
            v-model="localContract.validityPeriod.start"
            required
            :disabled="localContract.isExisting"
          />
        </VFormItem>

        <VSelectV2
          v-model="localContract.deliveryType"
          label="Тип доставки"
          layout="vertical"
          hide-clear-btn
          :data="DeliveryTypesService.deliveryTypes"
          required
        />

        <VSwitch
          v-model="localContract.isDeliveryPaidByBuyer"
          theme="slider"
          checked-label="Покупатель оплачивает доставку"
          unchecked-label="Продавец доставляет за свой счёт"
        />
      </section>

      <hr>

      <section class="partner">
        <header>Продавец</header>

        <VSelectV2
          v-model="localContract.seller"
          label="Партнер"
          layout="vertical"
          disabled
          :data="[localContract.seller]"
        />

        <VSelectV2
          v-model="localContract.sellerCompany"
          label="Юр. лицо"
          layout="vertical"
          hide-clear-btn
          :disabled="localContract.isExisting"
          :data="sellerCompanySource"
          required
        >
          <template #option="{ option }">
            {{ option.toString() }}
            <div class="description">
              {{ option.innDescription }}
            </div>
          </template>
        </VSelectV2>
      </section>

      <section class="partner">
        <header>Покупатель</header>

        <VSelectV2
          v-model="localContract.buyer"
          label="Партнер"
          layout="vertical"
          disabled
          :data="[localContract.buyer]"
        />

        <VSelectV2
          v-model="localContract.buyerCompany"
          label="Юр. лицо"
          layout="vertical"
          :disabled="localContract.isExisting"
          hide-clear-btn
          :data="buyerCompanySource"
          required
        >
          <template #option="{ option }">
            {{ option.toString() }}
            <div class="description">
              {{ option.innDescription }}
            </div>
          </template>
        </VSelectV2>
      </section>

      <hr>

      <section class="prices">
        <header>Цены</header>

        <VSwitch
          v-if="isVatPresented"
          v-model="localContract.isVatIncluded"
          :disabled="!isVatPresented"
          theme="slider"
          layout="vertical"
          checked-label="НДС включительно"
          unchecked-label="НДС сверху"
        />

        <VInput
          v-else
          layout="vertical"
          disabled
          value="НДС не облагается"
        />

        <!-- Старые договоры: признак НДС не сохранён, переключатель показывает «сверху» по умолчанию -->
        <p
          v-if="isVatNotSet"
          class="error"
        >
          В договоре не сохранено, как считается НДС (isVatIncluded: null) — пересохраните договор.
          Если НДС сверху, оставьте переключатель как есть.
        </p>
      </section>

      <hr>

      <PaymentTermEditor
        :entity="localContract"
        title="Оплата"
      />
    </template>
  </VDialogV3>
</template>

<style lang="scss">
/** Подсказка под переключателем НДС — типографика как у сообщений контролов (_contols.scss) */
#contract-external-card .prices > p.error {
  margin:      0;
  padding:     4px 0 0 0;
  font-size:   12px;
  line-height: 14px;
}
</style>

<script lang="ts">

import ContractExternal 				from "@/domains/Contracts/ContractExternal";
import ContractsExternalService from "@/domains/Contracts/ContractsExternalService";
import EntityPointer 						from "@/domains/EntityPointer/EntityPointer";
import DeliveryTypesService     from "@/domains/DeliveryTypes/DeliveryTypesService";
import CompaniesService         from "@/domains/Companies/CompaniesService";

import PaymentTermEditor 				from "@/domains/PaymentTerms/components/PaymentTermEditor/PaymentTermEditor.vue";
import FilesList 								from "@/core/services/Files/components/FilesList/FilesList.vue";

export default defineComponent({
  name: 'ContractExternalCard',

  components: {
    PaymentTermEditor,
    FilesList,
  },

  props: {
    /** ID договора — из роутера или ссылки; если договор не передан, карточка загрузит его сама */
    id: {
      type:    Number,
      default: null,
    },

    /** Готовый договор — без повторного запроса; новый договор (без id) передаётся только так */
    contract: {
      type:    ContractExternal,
      default: null,
    },
  },

  setup() {
    return {
      DeliveryTypesService,
    };
  },

  data() {
    /**
     * Правило прелоада зависимых источников документа:
     * - новый — preloadOnMount: опции готовы до первого клика,
     *   контрагентские подгрузятся сами при выборе партнёра;
     * - существующий — preloadOnFocus: шанс, что реквизиты станут
     *   менять, низок — впустую не грузим.
     *
     * Существующий — тот, что открыт по id: договор по id может ещё грузиться.
     */
    const isExisting = this.$core.utils.isNullOrUndefined(this.id) === false;

    return {
      ContractsExternalService,

      sellerCompanySource: this.$core.loader.select<ContractExternal>(({seller}) =>
        CompaniesService.getList({ partnerId: seller.id })
      )
        .dependsOn({seller: () => this.localContract.seller})
        .preloadOnMount(isExisting === false)
        .preloadOnFocus(isExisting === true),

      buyerCompanySource: this.$core.loader.select<ContractExternal>(({buyer}) =>
        CompaniesService.getList({ partnerId: buyer.id })
      )
        .dependsOn({buyer: () => this.localContract.buyer})
        .preloadOnMount(isExisting === false)
        .preloadOnFocus(isExisting === true),

      /** undefined, пока договор не загружен (init) — VDialogV3 показывает лоадер */
      localContract: undefined as unknown as ContractExternal,
      activeTab: {
        id: 'main',
        name: 'Основное'
      },
    };
  },

  mounted() {
    this.init();
  },

  computed: {
    submitDisabled(): string | null {
      /** блокируем сабмит пока данные не загрузятся в поля,
       * чтобы во время await не засабмитились старые значения */
      if (this.sellerCompanySource.isLoading === true || this.buyerCompanySource.isLoading === true) {
        return 'Загружаются данные';
      }
      return null;
    },

    isVatPresented(): boolean {
      return this.localContract?.sellerCompany?.isVatPayer === true;
    },

    /**
     * Старый договор: сохранён, поставщик платит НДС, а признак «НДС включительно / сверху»
     * не сохранён (null). Переключатель при этом показывает «НДС сверху» — договор нужно пересохранить.
     */
    isVatNotSet(): boolean {
      return this.localContract.isExisting === true
        && this.isVatPresented === true
        && this.localContract.isVatIncluded === null;
    },

    tabs(): { id: string; name: string }[] {
      /** Пока договор грузится (localContract undefined) — только «Основное» */
      return this.$core.currentPartner.isBuyer && this.localContract?.isExisting === true
        ? [{ id: 'main', name: 'Основное' }, { id: 'files', name: 'Файлы' }]
        : [{ id: 'main', name: 'Основное' }];
    },

    fileEntities(): EntityPointer[] {
      return [new EntityPointer('contractExternal', this.localContract.id, this.localContract.toString())];
    },
  },

  beforeUnmount() {
    this.$emit('component:before-unmount');
  },

  methods: {
    /**
     * Готовит локальную копию договора для редактирования. Договор берётся из пропа,
     * а если его нет — загружается по id.
     */

    async init(): Promise<void> {
      if (this.$core.utils.isNullOrUndefined(this.contract) === false) {
        this.localContract = this.$core.utils.cloneDeep(this.contract);
      }
      else {
        this.localContract = await ContractsExternalService.get(this.id);
      }
    },

    /**
     * Сохранение договора: создание или обновление.
     * Валидацию формы и resolve() при успехе делает сам VDialogV3.
     */

    async submit(): Promise<boolean> {
      /** Старый договор без признака НДС: переключатель показывает «НДС сверху» — сохраняем
       *  его, но только после подтверждения. Отказ — карточка остаётся открытой */
      if (this.isVatNotSet === true) {
        const isConfirmed = await this.$core.dialogs.confirm(
          'После сохранения в договоре будет «НДС сверху».',
          'НДС в договоре',
        );

        if (isConfirmed === true) {
          this.localContract.isVatIncluded = false;
        }
        else {
          return false;
        }
      }

      if (this.localContract.isExisting === true) {
        const success = await ContractsExternalService.update(this.localContract);
        if (success === true) {
          this.$emit(ContractExternal.Events.Updated, this.localContract);
        }
        return success;
      } else {
        const id = await ContractsExternalService.add(this.localContract);
        if (this.$core.utils.isNullOrUndefined(id) === false) {
          this.localContract.id = id;
          this.$emit(ContractExternal.Events.Created, this.localContract);
          return true;
        } else {
          return false;
        }
      }
    },
  },
});
</script>